import { useEffect, useRef, useState, type ReactNode } from "react";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { RequestError } from "@/app/queries";
import { funnelCSVHref, useAnalytics, useRecompute, type AnalyticsReport, type Period } from "@/app/analyticsQueries";
import { useCohorts, type Cohort } from "@/app/peopleQueries";
import { useMe } from "@/app/session";
import { Button } from "@/ui/Button";
import { NoAccess, NotFound, SkeletonRows, StateCard } from "@/ui/states";
import { Segmented } from "./mentor/queue/Segmented";
import { Feedback, Findings, NotMeasured } from "./admin/analytics/Cards";
import { Funnel } from "./admin/analytics/Funnel";
import { Kpis } from "./admin/analytics/Kpis";
import { Mentors } from "./admin/analytics/Mentors";
import { Pace } from "./admin/analytics/Pace";
import { PERIODS, SCOPE_ALL, cohortTabs, csvName, fmtComputed, funnelNote, joinFeedback, kpis } from "./admin/analytics/model";

/** A3 «Аналитика»: KPI, воронка по шагам, темп, менторы, отзывы на шагах,
 *  «чего мы не меряем», выводы, CSV и пересчёт. Период влияет только на темп и
 *  KPI «гейтов за период»; вкладки когорт включают завершённые. */
export function AnalyticsPage() {
  const me = useMe();
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const [scope, setScope] = useState(SCOPE_ALL);
  const [period, setPeriod] = useState<Period>("all");
  const report = useAnalytics(scope, period);
  const cohorts = useCohorts();
  const frame = (children: ReactNode) => (
    <Shell nav user={user}>
      {children}
    </Shell>
  );

  const err = report.error;
  if (err instanceof RequestError && err.status === 403)
    return frame(<NoAccess where="аналитика" body="Аналитику по курсу видят администраторы и владелец школы. Менторам доступна карта когорты, студентам — свой маршрут." primary={{ label: "на мой маршрут", href: "/" }} />);
  if (err instanceof RequestError && err.status === 404) return frame(<NotFound primary={{ label: "ко всем студентам", onClick: () => setScope(SCOPE_ALL) }} />);
  if (err)
    return frame(
      <StateCard
        code="ERR_NETWORK"
        where="аналитика"
        tone="warn"
        title="Нет связи с сервером"
        body="Отчёт не загрузился. Гейты, сдачи и вердикты хранятся на сервере — ничего не потеряно, пересчёт не нужен."
        primary={{ label: "повторить сейчас", onClick: () => void report.refetch() }}
      />,
    );
  if (!report.data) return frame(<SkeletonRows />);
  return <Analytics report={report.data} refetch={() => void report.refetch()} scope={scope} period={period} onScope={setScope} onPeriod={setPeriod} cohorts={cohorts.data?.cohorts} user={user} />;
}

function Analytics({
  report,
  refetch,
  scope,
  period,
  onScope,
  onPeriod,
  cohorts,
  user,
}: {
  report: AnalyticsReport;
  refetch: () => void;
  scope: string;
  period: Period;
  onScope: (s: string) => void;
  onPeriod: (p: Period) => void;
  cohorts?: Cohort[];
  user?: { name: string; role: string };
}) {
  const tabs = cohortTabs(cohorts);
  const recompute = useRecompute(scope);
  const [queuedAt, setQueuedAt] = useState<string>();
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Пересчёт идёт фоновой задачей: после 202 дотягиваем отчёт ещё пару раз, пока не сменится computed_at.
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const onRecompute = () =>
    recompute.mutate(undefined, {
      onSuccess: () => {
        setQueuedAt(report.computed_at);
        timers.current.forEach(clearTimeout);
        timers.current = [1500, 4000, 8000, 15000].map((ms) => setTimeout(refetch, ms));
      },
    });
  const updated = queuedAt !== undefined && queuedAt !== report.computed_at;

  return (
    <Shell nav wide user={user}>
      <div className="flex flex-wrap items-center gap-3 border-b border-bd px-4 py-2.5 md:px-[22px]">
        <Segmented label="когорта" value={scope} options={tabs} onChange={onScope} testPrefix="cohort" />
        <Segmented label="период" value={period} options={PERIODS} onChange={onPeriod} testPrefix="period" />
        <span className="flex-1" />
        <span className="font-mono text-[11px] text-mut" data-testid="computed-at">
          {updated ? "пересчитано" : "посчитано"} {fmtComputed(report.computed_at)}
          {queuedAt !== undefined && !updated && recompute.isSuccess ? " · пересчёт идёт" : ""}
        </span>
        <Button size="sm" onClick={onRecompute} disabled={recompute.isPending} reason={recompute.isPending ? "считаем…" : recompute.isError ? "не удалось поставить задачу — попробуйте ещё раз" : undefined} data-testid="recompute">
          пересчитать
        </Button>
        <a href={funnelCSVHref(scope, period)} download={csvName(scope, cohorts)} className="inline-flex items-center rounded-sm border border-bd2 bg-transparent px-[11px] py-[6px] font-mono text-[11px] leading-none text-ink no-underline hover:border-acc hover:text-acc" data-testid="csv-link">
          выгрузить csv
        </a>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_372px]">
        <div className="flex min-w-0 flex-col gap-[22px] px-4 pb-7 pt-[18px] md:px-[22px]">
          <h1 className="m-0 font-mono text-[20px] font-bold tracking-[-.015em]">Аналитика</h1>
          <Kpis items={kpis(report, scope, period)} />
          <Funnel report={report} note={funnelNote(report, scope, cohorts)} />
          <Mentors mentors={report.mentors} />
        </div>
        <aside className="flex flex-col gap-3.5 border-bd px-4 py-[18px] md:px-[18px] lg:border-l">
          <Pace report={report} period={period} />
          <Feedback rows={joinFeedback(report)} />
          <Findings />
          <NotMeasured text={report.not_measured} />
        </aside>
      </div>
    </Shell>
  );
}
